import scaleDrawing from "@/assets/binoculars-scale.svg";
import { createFileRoute, Link } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PRODUCT_BY_HANDLE_QUERY, storefrontApiRequest, normalizeMediaToGallery } from "@/lib/shopify";
import { SiteHeader } from "@/components/SiteHeader";
import { ProductGallery } from "@/components/ProductGallery";
import { ProductVideo } from "@/components/ProductVideo";


import binocularsModel from "@/assets/binoculars.glb.asset.json";
import { useCartStore } from "@/stores/cartStore";
import { trackMeta } from "@/lib/metaPixel";
import { SPEC_GROUPS } from "@/lib/specs";
import { OWNERS_MANUAL } from "@/lib/manual";

import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import BinocularViewerLazy from "@/components/BinocularViewerLazy";
import helmPosterAsset from "@/assets/helm-binocular-1600.webp.asset.json";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import finishBlue from "@/assets/finish-blue-thumb.webp.asset.json";
import finishBlack from "@/assets/finish-black-thumb.webp.asset.json";
import finishSilver from "@/assets/finish-silver-thumb.webp.asset.json";


const PRODUCT_HANDLE_ALIASES: Record<string, string> = {
  "6x16": "6x16-binoculars",
};

function resolveProductHandle(handle: string) {
  return PRODUCT_HANDLE_ALIASES[handle] ?? handle;
}

const FINISH_IMAGES: Record<string, string> = {
  blue: finishBlue.url,
  black: finishBlack.url,
  silver: finishSilver.url,
  grey: finishSilver.url,
  gray: finishSilver.url,
};

function finishImage(label: string) {
  const key = label.toLowerCase();
  const match = Object.keys(FINISH_IMAGES).find((k) => key.includes(k));
  return match ? FINISH_IMAGES[match] : undefined;
}

const money = (n: number) =>
  n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });



const productSearchSchema = z.object({
  color: z.string().optional(),
});

export const Route = createFileRoute("/product/$handle")({
  validateSearch: zodValidator(productSearchSchema),
  component: ProductPage,
  loader: async ({ params }) => {
    // Fetch minimal product data at SSR/prerender so head() can build rich JSON-LD.
    const handle = resolveProductHandle(params.handle);
    try {
      const res = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle });
      const p = res?.data?.productByHandle;
      if (!p) return { seed: null };
      const variant = p.variants?.edges?.[0]?.node;
      const image = p.images?.edges?.[0]?.node;
      return {
        seed: {
          title: p.title as string,
          description: p.description as string,
          image: image?.url as string | undefined,
          sku: variant?.id as string | undefined,
          priceAmount: p.priceRange?.minVariantPrice?.amount as string | undefined,
          priceCurrency: p.priceRange?.minVariantPrice?.currencyCode as string | undefined,
          availability: variant?.availableForSale ? "InStock" : "PreOrder",
        },
      };
    } catch {
      return { seed: null };
    }
  },
  head: ({ params, loaderData }) => {
    const resolvedHandle = resolveProductHandle(params.handle);
    const url = `https://doublelion.com/product/${resolvedHandle}`;
    const seed = loaderData?.seed;
    const name = seed?.title ?? resolvedHandle
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    const title = `${name} — Double Lion`.slice(0, 60);
    const description = (seed?.description || `${name} from Double Lion — a limited edition release of 300 Pieces, built for performance at sea and made to last.`).slice(0, 300);
    const productLd: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name,
      brand: { "@type": "Brand", name: "Double Lion" },
      url,
      description,
    };
    if (seed?.image) productLd.image = seed.image;
    if (seed?.sku) productLd.sku = seed.sku;
    if (seed?.priceAmount && seed?.priceCurrency) {
      productLd.offers = {
        "@type": "Offer",
        url,
        priceCurrency: seed.priceCurrency,
        price: seed.priceAmount,
        availability: `https://schema.org/${seed.availability ?? "InStock"}`,
        itemCondition: "https://schema.org/NewCondition",
      };
    }
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "product" },
        ...(seed?.image ? [{ property: "og:image", content: seed.image }, { property: "twitter:image", content: seed.image }] : []),
      ],
      links: [
        { rel: "canonical", href: url },
        ...(seed?.image ? [{ rel: "preload", as: "image", href: seed.image, fetchPriority: "high" as const }] : []),
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(productLd),
        },
      ],
    };
  },
  errorComponent: ({ error }) => (
    <div className="min-h-screen flex items-center justify-center font-mono text-xs uppercase tracking-widest">
      {error instanceof Error ? error.message : "Something went wrong"}
    </div>
  ),
  notFoundComponent: () => (
    <div className="min-h-screen flex items-center justify-center font-mono text-xs uppercase tracking-widest">
      Product not found
    </div>
  ),
});

function ProductPage() {
  const { handle } = Route.useParams();
  const shopifyHandle = resolveProductHandle(handle);
  const { color } = Route.useSearch();
  const addItem = useCartStore((s) => s.addItem);
  const isLoading = useCartStore((s) => s.isLoading);
  const [mounted, setMounted] = useState(false);
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const [focusedVariantId, setFocusedVariantId] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  useEffect(() => setMounted(true), []);


  const { data, isLoading: loading } = useQuery({
    queryKey: ["product", shopifyHandle],
    queryFn: async () => {
      const res = await storefrontApiRequest(PRODUCT_BY_HANDLE_QUERY, { handle: shopifyHandle });
      return res?.data?.productByHandle;
    },
  });

  // Focus the variant from the URL color if valid, else first available
  useEffect(() => {
    if (!data) return;
    const target = color
      ? data.variants.edges.find((e: any) =>
          e.node.selectedOptions.some(
            (o: any) => o.name.toLowerCase() === "color" && o.value === color,
          ),
        )?.node
      : undefined;
    const fallback =
      data.variants.edges.find((e: any) => e.node.availableForSale)?.node ||
      data.variants.edges[0]?.node;
    setFocusedVariantId((target || fallback)?.id ?? null);
  }, [data, color]);

  useEffect(() => {
    if (!data) return;
    const p = data.priceRange?.minVariantPrice;
    void trackMeta("ViewContent", {
      content_ids: [data.id],
      content_name: data.title,
      content_type: "product",
      value: p ? parseFloat(p.amount) : undefined,
      currency: p?.currencyCode,
    });
  }, [data?.id]);



  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="pt-32 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="pt-32 text-center">
          <p className="font-serif text-3xl">Product not found</p>
          <Link to="/" className="mt-6 inline-block font-mono text-[11px] uppercase tracking-[0.2em] underline">
            Back home
          </Link>
        </div>
      </div>
    );
  }

  const mediaItems = normalizeMediaToGallery(
    data.media?.edges?.map((e: any) => e.node) ?? [],
    data.title,
  );
  const images = data.images.edges.map((e: any) => e.node);
  const galleryItems = mediaItems.length > 0 ? mediaItems : images.map((img: any) => ({ kind: "image" as const, url: img.url, altText: img.altText }));
  const hasRealOptions =
    data.options?.length > 0 && !(data.options.length === 1 && data.options[0].name === "Title");

  const variantNodes: any[] = data.variants.edges.map((e: any) => e.node);
  const variant =
    variantNodes.find((v) => v.id === focusedVariantId) || variantNodes[0];

  const price = variant?.price || data.priceRange.minVariantPrice;
  const currency = price.currencyCode;

  const colorOf = (v: any) =>
    v.selectedOptions?.find((o: any) => o.name.toLowerCase() === "color")?.value ||
    v.title;

  const totalUnits = variantNodes.reduce((s, v) => s + (quantities[v.id] || 0), 0);
  const orderTotal = variantNodes.reduce(
    (s, v) => s + (quantities[v.id] || 0) * parseFloat(v.price.amount),
    0,
  );

  const setQty = (id: string, next: number) =>
    setQuantities((p) => ({ ...p, [id]: Math.max(0, next) }));

  const handleAdd = async () => {
    const selected = variantNodes.filter((v) => (quantities[v.id] || 0) > 0);
    if (selected.length === 0) return;
    setIsRedirecting(true);
    void trackMeta("AddToCart", {
      content_ids: selected.map((v) => v.id),
      content_type: "product",
      contents: selected.map((v) => ({ id: v.id, quantity: quantities[v.id] })),
      value: Number(orderTotal.toFixed(2)),
      currency,
    });
    try {
      for (const v of selected) {
        await addItem({
          product: { node: data },
          variantId: v.id,
          variantTitle: v.title,
          price: v.price,
          quantity: quantities[v.id],
          selectedOptions: v.selectedOptions || [],
        });
      }
      setQuantities({});
      const url = useCartStore.getState().checkoutUrl;
      if (url) {
        window.location.assign(url);
        return;
      }
      toast.error("Checkout unavailable", {
        description: "Please try again in a moment.",
      });
    } catch (e) {
      console.error(e);
      toast.error("Checkout unavailable", {
        description: "Please try again in a moment.",
      });
    }
    setIsRedirecting(false);
  };



  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="pt-16 md:pt-24 pb-24">
        {/* Top: gallery + summary */}
        <section className="px-6">
          <div className="mx-auto max-w-7xl">
            <div className="grid md:grid-cols-2 gap-6 md:gap-16">
              <ProductGallery items={galleryItems} title={data.title} activeImageUrl={variant?.image?.url} />




              <div className="flex flex-col md:pt-4">
                <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
                  {data.title}
                </h1>
                <p className="mt-4 font-mono text-base">
                  {currency} {parseFloat(price.amount).toFixed(2)} <span className="text-muted-foreground">ea</span>
                </p>
                <div className="order-last md:order-2 mt-8 font-sans text-base text-foreground/80 leading-relaxed whitespace-pre-line">
                  {data.description}
                </div>

                <div className="mt-2 md:mt-0 md:order-3" style={{ zoom: 0.82 }}>
                <div className="mt-6 rounded-[14px] overflow-hidden border border-[rgba(250,249,246,0.14)] bg-[#1E2A44] shadow-[0_1px_0_rgba(20,22,26,0.06),0_18px_40px_-28px_rgba(20,22,26,0.55)]">
                  <div className="grid grid-cols-[1fr_auto_72px] items-center gap-4 px-5 pt-3.5 pb-3 border-b border-[rgba(233,238,248,0.2)] font-mono text-[10.5px] uppercase tracking-[0.24em] text-[rgba(234,239,248,0.65)]">
                    <span>{hasRealOptions ? "Finish" : "Item"}</span>
                    <span className="w-[112px] text-center">Qty</span>
                    <span className="text-right">Sub</span>
                  </div>

                  {variantNodes.map((v) => {
                    const qty = quantities[v.id] || 0;
                    const on = qty > 0;
                    const label = colorOf(v);
                    const img = finishImage(label);
                    return (
                      <div
                        key={v.id}
                        className={`relative grid grid-cols-[1fr_auto_72px] items-center gap-4 px-5 h-[66px] border-b border-[rgba(233,238,248,0.13)] transition-colors ${
                          on ? "bg-[rgba(255,255,255,0.09)]" : ""
                        }`}
                      >
                        <span
                          aria-hidden
                          className={`absolute left-0 top-0 bottom-0 w-[3px] transition-colors ${
                            on ? "bg-[#E8B24C]" : "bg-transparent"
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => setFocusedVariantId(v.id)}
                          className="flex items-center gap-3.5 text-left min-w-0"
                        >
                          <span className="w-11 h-8 flex-none flex items-center justify-center rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.16),rgba(255,255,255,0))]">
                            {img ? (
                              <img
                                src={img}
                                alt={label}
                                width={40}
                                height={28}
                                loading="eager"
                                decoding="async"
                                className={`w-10 h-7 object-contain drop-shadow-[0_1px_3px_rgba(0,0,0,0.35)] transition-opacity ${
                                  on ? "opacity-100" : "opacity-[0.78]"
                                }`}
                              />
                            ) : null}
                          </span>
                          <span
                            className={`text-[16px] tracking-[-0.01em] whitespace-nowrap ${
                              on ? "font-semibold text-white" : "text-[rgba(234,239,248,0.82)]"
                            }`}
                          >
                            {label}
                          </span>
                        </button>

                        <div className="flex items-center justify-between w-[112px] h-[38px] rounded-full border border-[rgba(233,238,248,0.3)] bg-[rgba(255,255,255,0.1)]">
                          <button
                            type="button"
                            onClick={() => setQty(v.id, qty - 1)}
                            disabled={qty <= 0}
                            className="w-9 h-9 mx-px rounded-full font-mono text-lg leading-none text-[#EAEFF8] hover:bg-[rgba(255,255,255,0.22)] active:bg-[rgba(255,255,255,0.34)] disabled:opacity-30 transition-colors"
                            aria-label={`Decrease ${label} quantity`}
                          >
                            −
                          </button>
                          <span
                            className={`font-mono text-[16px] font-medium tabular-nums ${
                              on ? "text-white" : "text-[rgba(234,239,248,0.8)]"
                            }`}
                          >
                            {qty}
                          </span>
                          <button
                            type="button"
                            onClick={() => setQty(v.id, qty + 1)}
                            className="w-9 h-9 mx-px rounded-full font-mono text-lg leading-none text-[#EAEFF8] hover:bg-[rgba(255,255,255,0.22)] active:bg-[rgba(255,255,255,0.34)] transition-colors"
                            aria-label={`Increase ${label} quantity`}
                          >
                            +
                          </button>
                        </div>

                        <span
                          className={`text-right font-mono text-[13.5px] tabular-nums ${
                            on ? "text-white" : "text-[rgba(234,239,248,0.45)]"
                          }`}
                        >
                          {on ? money(qty * parseFloat(v.price.amount)) : "—"}
                        </span>
                      </div>
                    );
                  })}

                  <div className="flex items-end justify-between gap-4 px-5 pt-4 pb-[17px]">
                    <span className="font-mono text-[10.5px] uppercase tracking-[0.2em] text-[rgba(234,239,248,0.9)]">
                      {totalUnits} unit{totalUnits === 1 ? "" : "s"}
                    </span>
                    <span className="flex items-baseline gap-[7px]">
                      <span className="font-mono text-[10.5px] tracking-[0.2em] text-[rgba(234,239,248,0.65)]">
                        {currency}
                      </span>
                      <span className="font-mono text-[25px] tracking-[-0.02em] tabular-nums text-white">
                        {money(orderTotal)}
                      </span>
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleAdd}
                  disabled={isLoading || isRedirecting || totalUnits === 0}
                  className={`mt-2.5 w-full h-[52px] rounded-full flex items-center justify-center gap-2.5 font-mono text-[13px] uppercase tracking-[0.28em] transition-all hover:-translate-y-px active:translate-y-0 disabled:cursor-not-allowed ${
                    totalUnits > 0
                      ? "bg-[#14161A] text-[#FAF9F6]"
                      : "bg-[#6E727B] text-[rgba(250,249,246,0.85)]"
                  }`}
                >
                  {isLoading || isRedirecting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Preparing checkout</span>
                    </>
                  ) : (
                    <>
                      <span>Purchase</span>
                      <span className="text-xs tracking-normal">↗</span>
                    </>
                  )}
                </button>

                <div className="mt-3 text-center font-mono text-[10.5px] uppercase tracking-[0.2em] text-muted-foreground">
                  {"\n"}
                </div>
                </div>





              </div>
            </div>
          </div>
        </section>

        {/* Full specifications accordion */}
        <section id="specs" className="px-6 mt-16 md:mt-24">
          <div className="mx-auto max-w-7xl">
            <div className="flex items-baseline justify-between gap-6 border-b border-foreground/20 pb-4 mb-2">
              <h2 className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-foreground">
                Full specifications
              </h2>
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                {SPEC_GROUPS.length} groups
              </span>
            </div>




            <Accordion
              type="multiple"
              defaultValue={["Optics"]}
              className="divide-y divide-border"
            >
              {SPEC_GROUPS.map((group) => (
                <AccordionItem key={group.heading} value={group.heading} className="border-b-0">
                  <AccordionTrigger className="hover:no-underline py-6 group">
                    <span className="font-mono text-sm md:text-base uppercase tracking-[0.25em] text-foreground">
                      {group.heading}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pb-6">
                      {group.rows.map(([k, v]) => (
                        <div
                          key={k}
                          className="rounded-md bg-muted/40 border border-border/60 px-4 py-3 flex flex-col gap-1"
                        >
                          <dt className="font-mono text-[11px] md:text-xs uppercase tracking-[0.15em] text-muted-foreground">
                            {k}
                          </dt>
                          <dd className="font-serif text-lg md:text-xl leading-snug text-foreground">
                            {v}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {OWNERS_MANUAL && (
              <div className="pt-5">
                <a
                  href={OWNERS_MANUAL.url}
                  download
                  target="_blank"
                  rel="noopener"
                  className="inline-flex w-full sm:w-[30%] sm:min-w-[210px] items-center gap-2 rounded-[10px] bg-[#1E2A44] px-3 py-3 font-mono text-[10px] leading-[1.5] uppercase tracking-[0.16em] text-[rgba(234,239,248,0.85)] hover:text-white shadow-[0_12px_28px_-22px_rgba(20,22,26,0.55)] transition-colors"
                >
                  <span aria-hidden className="text-[#E8B24C]">↓</span>
                  <span>{OWNERS_MANUAL.title}</span>
                  <span className="ml-auto text-[rgba(234,239,248,0.55)]">
                    {OWNERS_MANUAL.fileType} · {OWNERS_MANUAL.size}
                  </span>
                </a>
              </div>
            )}
          </div>
        </section>





        {/* Video */}
        <section className="px-6 mt-32 md:mt-48">
          <div className="mx-auto max-w-6xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-6">
              In motion
            </div>
            <ProductVideo />
          </div>
        </section>

        {/* 3D Viewer */}
        <section id="viewer" className="px-6 mt-32 md:mt-48">
          <div className="mx-auto max-w-6xl">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground mb-4">
              Inspect
            </div>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight mb-12 max-w-3xl">
              Precision Assembly.
            </h2>
            {mounted && <BinocularViewerLazy modelSrc={binocularsModel.url} poster={helmPosterAsset.url} />}
          </div>
        </section>

      </main>
    </div>
  );
}
